import { inject } from '@angular/core';
import { AppStateService, CartService, CheckoutService, GoogleAnalyticsService, HttpErrorService } from '../../../services';
import { distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CheckoutTotals, ProcessOrderResponse } from '../../../models';
import { GAEventAction, PaymentStatus, PaymentType } from '../../../enums';
import { MessageService } from 'primeng/api';
import { NavigationExtras, Router } from '@angular/router';
import { getItemPrice } from '../../../pipes';

/** when extending this class be sure to implement ngOnInit,
 * @Input({ required: true })
 * override completeUrl: string = '';
 * 
 * @Input()
 * override iAgreeMessage: string = '';
 */
export abstract class NgxAimOverviewClass {
  cartService = inject(CartService);
  checkoutService = inject(CheckoutService);
  httpErrorService = inject(HttpErrorService);
  appState = inject(AppStateService);
  messageService = inject(MessageService);
  router = inject(Router);
  gaService = inject(GoogleAnalyticsService);

  completeUrl: string = '';
  iAgreeMessage: string = '';
  iAgree = false;

  totals$ = this.checkoutService.totals$.pipe(
    distinctUntilChanged(),
    takeUntilDestroyed(),
  );
  totals: CheckoutTotals = {
    items: 0,
    shipping: 0,
    tax: 0,
    total: 0,
  }
  cart = this.cartService.getCart();
  orderIdJwt: string | undefined;
  isProcessing = false;
  showError = false;

  init() {
    this.totals$.subscribe(totals => {
      this.totals = totals;
    });

    if (!this.iAgreeMessage) {
      this.iAgree = true;
    }
  }

  onCompleteClick() {
    if (this.appState.paymentType === PaymentType.NONE && this.totals.total !== 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Failed',
        detail: 'Cannot not check out at this time. No payment processor',
        key: 'app-toast'
      });
      return;
    }

    this.isProcessing = true;
    this.checkoutService.checkout(this.orderIdJwt).subscribe({
      next: (res) => this.handleCheckoutResponse(res),
      error: (err) => this.onError(err),
    });
  }

  handleCheckoutResponse(res: ProcessOrderResponse) {
    this.isProcessing = false;
    this.orderIdJwt = res.orderIdJwt;
    if (res.paymentStatus === PaymentStatus.APPROVED) {
      this.emitGaEvent(res);
      this.cartService.reset();
      this.checkoutService.reset();

      const navigationExtras: NavigationExtras = {
        state: {
          orderId: res.orderId,
        },
      }
      this.router.navigate(['/', this.completeUrl], navigationExtras);
    } else {
      this.showError = true;
    }
  }

  onIAgreeMessageClick() {
    this.iAgree = !this.iAgree;
  }

  emitGaEvent(res: ProcessOrderResponse) {
    const items: any[] = [];
    this.cart.items.forEach((item) => {
      items.push({
        item_id: item.Model,
        item_name: item.SETitle || item.Model,
        item_brand: item.Manufacturer,
        price: getItemPrice(item, this.appState.getUserPriceLevel()),
        quantity: item.cartQty,
      });
    });

    this.gaService.emitEvent(GAEventAction.PURCHASE, {
      transaction_id: res.orderId,
      currency: 'USD',
      value: this.totals.items,
      tax: this.totals.tax,
      shipping: this.totals.shipping,
      items: items,
    });
  }

  onError(err: any) {
    this.isProcessing = false;
    this.httpErrorService.onHttpError(err, `We couldn't process your order`);
  }
}
